'use client';

import { createContext, useContext, useMemo, useState } from 'react';

export type Locale = 'en' | 'ro';

export interface BilingualContent {
  en: string;
  ro: string;
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (content: BilingualContent) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  children,
  defaultLocale = 'en',
}: {
  children: React.ReactNode;
  defaultLocale?: Locale;
}) {
  const [locale, setLocale] = useState<Locale>(defaultLocale);

  // Stable context value: without useMemo, a fresh { locale, setLocale, t } object (and a
  // fresh `t`) is allocated every render, so ANY re-render of this provider re-renders every
  // t() consumer — nearly the whole page. Memoizing pins the identity so it changes ONLY when
  // `locale` changes (the one case where consumers must re-render).
  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t: (content: BilingualContent) => content[locale] }),
    [locale]
  );

  return <LocaleContext value={value}>{children}</LocaleContext>;
}

export function useLocale(): LocaleContextValue {
  // eslint-disable-next-line @eslint-react/no-use-context -- deferred to React 19 use() migration
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used inside <LocaleProvider>');
  }
  return ctx;
}
